import { Helmet } from "react-helmet-async";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../Utils/Axios";

function formatDateTimeLocal(isoString) {
  if (!isoString) return '';
  const d = new Date(isoString);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => n.toString().padStart(2, '0');
  const yyyy = d.getFullYear();
  const mm = pad(d.getMonth() + 1);
  const dd = pad(d.getDate());
  const hh = pad(d.getHours());
  const mi = pad(d.getMinutes());
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
}

function AdminCompanyDashboardManagement() {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [pagePermissions, setPagePermissions] = useState({});
  const [dashboardType, setDashboardType] = useState('main');
  const [offlinePassKey, setOfflinePassKey] = useState('');
  const [onlinePassKey, setOnlinePassKey] = useState('');
  const [offlinePassKeyExpiresAt, setOfflinePassKeyExpiresAt] = useState('');
  const [onlinePassKeyExpiresAt, setOnlinePassKeyExpiresAt] = useState('');
  const [deviceSessions, setDeviceSessions] = useState([]);
  const [isLoadingDevices, setIsLoadingDevices] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Pages for Main Dashboard (and English dashboard)
  const mainDashboardPages = [
    { key: 'overview', labelEn: 'Overview', labelUr: 'نظارہ', type: 'tab' },
    { key: 'profile', labelEn: 'Profile', labelUr: 'پروفائل', type: 'tab' },
    { key: 'persons', labelEn: 'Contact', labelUr: 'رابطہ', type: 'tab' },
    { key: 'bills', labelEn: 'Bills', labelUr: 'بلز', type: 'tab' },
    { key: 'sales', labelEn: 'Sales', labelUr: 'سیلز', type: 'tab' },
    { key: 'recoveries', labelEn: 'Recoveries', labelUr: 'ریکوریز', type: 'tab' },
    { key: 'recovery/records', labelEn: 'Recovery Records', labelUr: 'ریکوری ریکارڈ', type: 'page' },
    { key: 'roz-namacha', labelEn: 'Settled Bill', labelUr: 'بل ادائیگی', type: 'page' },
    { key: 'roznamacha', labelEn: 'Roz Namacha', labelUr: 'روزنامچہ', type: 'page' },
    { key: 'expense-fields-details', labelEn: 'Expense Details', labelUr: 'اخراجات کی تفصیلات', type: 'page' },
    { key: 'stock-report', labelEn: 'Stock Report', labelUr: 'اسٹاک رپورٹ', type: 'page' },
    { key: 'user-manage', labelEn: 'User Manage', labelUr: 'یوزر مینجمنٹ', type: 'page' },
    { key: 'receipts', labelEn: 'Receipt Management', labelUr: 'رسید مینجمنٹ', type: 'tab' },
    { key: 'ledgers', labelEn: 'Ledgers', labelUr: 'کھاتے', type: 'tab' },
  ];

  // Pages for Part2 / Urdu Dashboard
  const part2DashboardPages = [
    { key: 'overview', labelEn: 'Overview', labelUr: 'نظارہ', type: 'tab' },
    { key: 'profile', labelEn: 'Profile', labelUr: 'پروفائل', type: 'tab' },
    { key: 'persons', labelEn: 'Contact', labelUr: 'رابطہ', type: 'tab' },
    { key: 'create-bill-part2', labelEn: 'Create Bill', labelUr: 'بل بنائیں', type: 'page' },
    { key: 'sales', labelEn: 'Sales', labelUr: 'سیلز', type: 'tab' },
    { key: 'recovery-create', labelEn: 'Recoveries', labelUr: 'ریکوریز', type: 'page' },
    { key: 'roz-namacha', labelEn: 'Settled Bill', labelUr: 'بل ادائیگی', type: 'page' },
    { key: 'roznamacha', labelEn: 'Roz Namacha', labelUr: 'روزنامچہ', type: 'page' },
    { key: 'expense-fields-details', labelEn: 'Expense Details', labelUr: 'اخراجات کی تفصیلات', type: 'page' },
    { key: 'stock-report', labelEn: 'Stock Report', labelUr: 'اسٹاک رپورٹ', type: 'page' },
    { key: 'user-manage', labelEn: 'User Manage', labelUr: 'یوزر مینجمنٹ', type: 'page' },
    { key: 'receipts', labelEn: 'Receipt Management', labelUr: 'رسید مینجمنٹ', type: 'tab' },
    { key: 'customer-khata-p2', labelEn: 'Customer Khata', labelUr: 'کسٹمر کھاتہ', type: 'page' },
    { key: 'supplier-khata-p2', labelEn: 'Supplier Khata', labelUr: 'سپلائر کھاتہ', type: 'page' },
    { key: 'bank-khata-p2', labelEn: 'Bank Khata', labelUr: 'بینک کھاتہ', type: 'page' },
    { key: 'expense-ledger-p2', labelEn: 'Expense Ledger', labelUr: 'اخراجات کھاتہ', type: 'page' },
    { key: 'receivable-khata-p2', labelEn: 'Receivable Khata', labelUr: 'قابل وصول کھاتہ', type: 'page' },
    { key: 'payable-khata-p2', labelEn: 'Payable Khata', labelUr: 'قابل ادائیگی کھاتہ', type: 'page' },
    { key: 'profit-loss-p2', labelEn: 'Profit & Loss', labelUr: 'منافع و نقصان', type: 'page' },
    { key: 'akhrajat-book-p2', labelEn: 'Akhrajat Book', labelUr: 'اخراجات بُک', type: 'page' },
    { key: 'amdani-book-p2', labelEn: 'Amdani Book', labelUr: 'آمدنی بُک', type: 'page' },
  ];

  // Show only pages for the selected dashboard type
  const isPart2 = dashboardType === 'part2';
  const availablePages = isPart2 ? part2DashboardPages : mainDashboardPages;
  const allPagesForFetch = [...mainDashboardPages, ...part2DashboardPages];
  const allPageKeys = [...new Set(allPagesForFetch.map((p) => p.key))];

  useEffect(() => {
    fetchCompanies();
  }, []);

  useEffect(() => {
    if (selectedCompany) {
      fetchPagePermissions(selectedCompany);
      const company = companies.find(c => c._id === selectedCompany);
      if (company) {
        setDashboardType(company.dashboardType || 'main');
        setOfflinePassKey(company.offlinePassKey || '');
        setOnlinePassKey(company.onlinePassKey || '');
        setOfflinePassKeyExpiresAt(
          formatDateTimeLocal(company.offlinePassKeyExpiresAt)
        );
        setOnlinePassKeyExpiresAt(
          formatDateTimeLocal(company.onlinePassKeyExpiresAt)
        );
      }
      fetchDeviceSessions(selectedCompany);
    } else {
      setPagePermissions({});
      setDeviceSessions([]);
    }
  }, [selectedCompany, companies]);

  const fetchCompanies = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem("adminToken");
      if (!token) {
        navigate("/admin/login");
        return;
      }

      const response = await api.get("/admin/companies", {
        headers: { Authorization: `Bearer ${token}` }
      });

      setCompanies(response.data.companies || []);
    } catch (error) {
      console.error("Fetch companies error:", error);
      toast.error("Failed to fetch companies");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPagePermissions = async (companyId) => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem("adminToken");
      const response = await api.get(`/admin/companies/${companyId}/page-permissions`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const permissions = {};
      allPageKeys.forEach(key => {
        permissions[key] = response.data.pagePermissions?.[key] !== false;
      });
      setPagePermissions(permissions);
    } catch (error) {
      console.error("Fetch page permissions error:", error);
      const permissions = {};
      allPageKeys.forEach(key => {
        permissions[key] = true;
      });
      setPagePermissions(permissions);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDeviceSessions = async (companyId) => {
    try {
      setIsLoadingDevices(true);
      const token = localStorage.getItem("adminToken");
      const response = await api.get(`/admin/companies/${companyId}/devices`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDeviceSessions(response.data.sessions || []);
    } catch (error) {
      console.error("Fetch device sessions error:", error);
      setDeviceSessions([]);
    } finally {
      setIsLoadingDevices(false);
    }
  };

  const handleTogglePermission = (pageKey) => {
    setPagePermissions(prev => ({
      ...prev,
      [pageKey]: !prev[pageKey]
    }));
  };

  const handleSave = async () => {
    if (!selectedCompany) {
      toast.error("Please select a company");
      return;
    }

    try {
      setIsSaving(true);
      const token = localStorage.getItem("adminToken");

      // Save only permissions for the currently selected dashboard's pages (so other dashboard's permissions are not overwritten)
      const permissionsToSave = {};
      availablePages.forEach(page => {
        permissionsToSave[page.key] = pagePermissions[page.key] !== false;
      });
      await api.put(`/admin/companies/${selectedCompany}/page-permissions`, {
        pagePermissions: permissionsToSave
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Save dashboard type
      await api.put(`/admin/companies/${selectedCompany}`, {
        dashboardType,
        offlinePassKey,
        onlinePassKey,
        offlinePassKeyExpiresAt,
        onlinePassKeyExpiresAt
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success("Settings updated successfully!");
      fetchCompanies(); // Refresh to get updated data
    } catch (error) {
      console.error("Save settings error:", error);
      toast.error("Failed to save settings");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSelectAll = () => {
    setPagePermissions(prev => ({
      ...prev,
      ...Object.fromEntries(availablePages.map(page => [page.key, true]))
    }));
  };

  const handleDeselectAll = () => {
    setPagePermissions(prev => ({
      ...prev,
      ...Object.fromEntries(availablePages.map(page => [page.key, false]))
    }));
  };

  const selectedCompanyData = companies.find(c => c._id === selectedCompany);

  return (
    <>
      <Helmet>
        <title>Company Dashboard Management - Admin</title>
      </Helmet>
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white shadow-sm border-b">
          <div className="max-w-7xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Company Dashboard Management</h1>
                <p className="text-sm text-gray-600 mt-1">Manage page visibility for company dashboards</p>
              </div>
              <button
                onClick={() => navigate('/admin')}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-all"
              >
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 py-8">
          {/* Company Selection */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Company
            </label>
            <select
              value={selectedCompany || ''}
              onChange={(e) => setSelectedCompany(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            >
              <option value="">-- Select a Company --</option>
              {companies.map(company => (
                <option key={company._id} value={company._id}>
                  {company.shopName || company.username} ({company.email})
                </option>
              ))}
            </select>
          </div>

          {selectedCompany && (
            <>
              {/* Company Info */}
              <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  Company: {selectedCompanyData?.shopName || selectedCompanyData?.username}
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-4">
                  <div>
                    <span className="text-gray-600">Email:</span>
                    <p className="font-medium">{selectedCompanyData?.email}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">Username:</span>
                    <p className="font-medium">{selectedCompanyData?.username}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">Status:</span>
                    <p className={`font-medium ${selectedCompanyData?.isActive ? 'text-green-600' : 'text-red-600'}`}>
                      {selectedCompanyData?.isActive ? 'Active' : 'Inactive'}
                    </p>
                  </div>
                  <div>
                    <span className="text-gray-600">Language:</span>
                    <p className="font-medium">{selectedCompanyData?.language || 'en'}</p>
                  </div>
                </div>

                {/* Passkeys */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Offline Passkey
                    </label>
                    <input
                      type="password"
                      value={offlinePassKey}
                      onChange={(e) => setOfflinePassKey(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      placeholder="Optional offline key for desktop app"
                    />
                    <label className="block text-xs text-gray-600 mt-2 mb-1">
                      Offline Passkey Expiry (Date &amp; Time)
                    </label>
                    <input
                      type="datetime-local"
                      value={offlinePassKeyExpiresAt}
                      onChange={(e) => setOfflinePassKeyExpiresAt(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Online Passkey
                    </label>
                    <input
                      type="password"
                      value={onlinePassKey}
                      onChange={(e) => setOnlinePassKey(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                      placeholder="Optional online key for desktop app"
                    />
                    <label className="block text-xs text-gray-600 mt-2 mb-1">
                      Online Passkey Expiry (Date &amp; Time)
                    </label>
                    <input
                      type="datetime-local"
                      value={onlinePassKeyExpiresAt}
                      onChange={(e) => setOnlinePassKeyExpiresAt(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    />
                  </div>
                </div>

                {/* Dashboard Type Selection */}
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Dashboard Type
                  </label>
                  <select
                    value={dashboardType}
                    onChange={(e) => setDashboardType(e.target.value)}
                    className="w-full md:w-64 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  >
                    <option value="main">Main Dashboard (Standard)</option>
                    <option value="part2">Urdu Dashboard (Part 2)</option>
                    <option value="en">English Dashboard</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Select which dashboard this company will use after login
                  </p>
                </div>

                {/* Device sessions summary */}
                <div className="mt-6 pt-4 border-t border-gray-200">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-semibold text-gray-800">
                      Desktop Device Sessions
                    </h3>
                    <button
                      onClick={() => fetchDeviceSessions(selectedCompany)}
                      className="px-3 py-1 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg border border-gray-300 transition-all"
                    >
                      View / Refresh
                    </button>
                  </div>
                  {isLoadingDevices ? (
                    <p className="text-xs text-gray-500">
                      Loading device sessions...
                    </p>
                  ) : deviceSessions.length === 0 ? (
                    <p className="text-xs text-gray-500">
                      No desktop logins recorded yet for this company.
                    </p>
                  ) : (
                    <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg">
                      <table className="min-w-full text-xs">
                        <thead className="bg-gray-100 text-gray-700">
                          <tr>
                            <th className="px-3 py-2 text-left">When</th>
                            <th className="px-3 py-2 text-left">Mode</th>
                            <th className="px-3 py-2 text-left">IP</th>
                            <th className="px-3 py-2 text-left">OS</th>
                            <th className="px-3 py-2 text-left">App</th>
                          </tr>
                        </thead>
                        <tbody>
                          {deviceSessions.map((s) => (
                            <tr key={s._id} className="border-t border-gray-200">
                              <td className="px-3 py-2">
                                {new Date(s.createdAt).toLocaleString()}
                              </td>
                              <td className="px-3 py-2 capitalize">
                                {s.mode || '-'}
                              </td>
                              <td className="px-3 py-2">{s.ip || '-'}</td>
                              <td className="px-3 py-2">{s.os || '-'}</td>
                              <td className="px-3 py-2">{s.appVersion || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>

              {/* Page Permissions - only show pages for selected dashboard */}
              <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-900">Page Permissions</h2>
                    <p className="text-sm text-gray-500 mt-0.5">
                      {isPart2 ? 'Urdu Dashboard (Part 2) pages' : dashboardType === 'en' ? 'English Dashboard pages' : 'Main Dashboard pages'}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={handleSelectAll}
                      className="px-3 py-1 bg-green-500 hover:bg-green-600 text-white text-sm rounded-lg transition-all"
                    >
                      Enable All
                    </button>
                    <button
                      onClick={handleDeselectAll}
                      className="px-3 py-1 bg-red-500 hover:bg-red-600 text-white text-sm rounded-lg transition-all"
                    >
                      Disable All
                    </button>
                  </div>
                </div>

                {isLoading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto"></div>
                    <p className="text-gray-600 mt-2">Loading permissions...</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {availablePages.map(page => (
                      <div
                        key={page.key}
                        className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <span className={`px-2 py-1 text-xs rounded ${page.type === 'tab'
                              ? 'bg-blue-100 text-blue-700'
                              : 'bg-purple-100 text-purple-700'
                            }`}>
                            {page.type === 'tab' ? 'Tab' : 'Page'}
                          </span>
                          <div>
                            <p className="font-medium text-gray-900">{page.labelEn}</p>
                            <p className="text-sm text-gray-500">{page.labelUr}</p>
                          </div>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={pagePermissions[page.key] !== false}
                            onChange={() => handleTogglePermission(page.key)}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                          <span className="ml-3 text-sm font-medium text-gray-700">
                            {pagePermissions[page.key] !== false ? 'Enabled' : 'Disabled'}
                          </span>
                        </label>
                      </div>
                    ))}
                  </div>
                )}

                <div className="mt-6 pt-6 border-t border-gray-200">
                  <button
                    onClick={handleSave}
                    disabled={isSaving}
                    className="w-full px-6 py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {isSaving ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        <span>Saving...</span>
                      </>
                    ) : (
                      <>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span>Save Permissions</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </>
          )}

          {!selectedCompany && (
            <div className="bg-white rounded-lg shadow-md p-12 text-center">
              <svg className="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <p className="text-xl text-gray-600">Please select a company to manage page permissions</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default AdminCompanyDashboardManagement;

